import { motion, AnimatePresence } from 'framer-motion';
import { useWorkerInfo } from '@/hooks/useWorkerInfo';
import { CategoryType } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import {
  User,
  Briefcase,
  GraduationCap,
  Zap,
  FileText,
  CheckCircle,
  Circle,
  ArrowLeft
} from 'lucide-react';

import { PersonalCategory } from './categories/PersonalCategory';
import { ExperienceCategory } from './categories/ExperienceCategory';
import { EducationCategory } from './categories/EducationCategory';
import { SkillsCategory } from './categories/SkillsCategory';
import { DocumentationCategory } from './categories/DocumentationCategory';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';

export function InfoLayout({ workerId }: { workerId: string }) {
  const navigate = useNavigate();
  const { categories, activeCategory, changeCategory, getCategoryProgress, loading, updateAnswer, saving } = useWorkerInfo(workerId);
  const currentIndex = categories.findIndex(
    (c) => c.category === activeCategory
  );
  const goNext = () => {
    if (!isLast) {
      const nextCategory = categories[currentIndex + 1];
      changeCategory(nextCategory.category);
    } else {
      handleFinish();
    }
  };

  const goBack = () => {
    if (!isFirst) {
      const prevCategory = categories[currentIndex - 1];
      changeCategory(prevCategory.category);
    }
  };

  const handleFinish = () => {
    navigate('/dashboard');
  };

  const handleBack = () => {
    navigate('/dashboard');
  };
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === categories.length - 1;

  const categoryIcons: Record<CategoryType, { icon: JSX.Element; color: string }> = {
    personal: { icon: <User size={16} />, color: '#3b82f6' },
    experience: { icon: <Briefcase size={16} />, color: '#8b5cf6' },
    education: { icon: <GraduationCap size={16} />, color: '#ec4899' },
    skills: { icon: <Zap size={16} />, color: '#f59e0b' },
    documentation: { icon: <FileText size={16} />, color: '#14b8a6' },
  };

  const totalProgress = categories.length > 0
    ? Math.round(categories.reduce((acc, cat) => acc + getCategoryProgress(cat.category), 0) / categories.length)
    : 0;

  const renderCategoryContent = () => {
    const categoryData = categories.find((c) => c.category === activeCategory);
    if (!categoryData) return null;

    const progress = getCategoryProgress(activeCategory);

    switch (activeCategory) {
      case 'personal':
        return (
          <PersonalCategory
            answers={categoryData.answers}
            progress={progress}
            onUpdateAnswer={updateAnswer}
            saving={saving}
          />
        );
      case 'experience':
        return (
          <ExperienceCategory
            answers={categoryData.answers}
            progress={progress}
            onUpdateAnswer={updateAnswer}
            saving={saving}
          />
        );
      case 'education':
        return (
          <EducationCategory
            answers={categoryData.answers}
            progress={progress}
            onUpdateAnswer={updateAnswer}
            saving={saving}
          />
        );
      case 'skills':
        return (
          <SkillsCategory
            answers={categoryData.answers}
            progress={progress}
            onUpdateAnswer={updateAnswer}
            saving={saving}
          />
        );
      case 'documentation':
        return (
          <DocumentationCategory
            answers={categoryData.answers}
            progress={progress}
            onUpdateAnswer={updateAnswer}
            saving={saving}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: colors.gray[50],
        }}
      >
        <div>Cargando...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(180deg, ${colors.primary[50]} 0%, ${colors.gray[50]} 100%)`,
        display: 'flex',
        justifyContent: 'center',
        padding: `${spacing[16]} ${spacing[3]} ${spacing[3]}`,
      }}
    >
      {/* Cabecera fija */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${colors.gray[200]}`,
          padding: spacing[3],
        }}
      >
        <div
          style={{
            maxWidth: 430,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button
            onClick={handleBack}
            style={{
              width: 36,
              height: 36,
              borderRadius: 12,
              border: `1px solid ${colors.gray[200]}`,
              backgroundColor: colors.white,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <ArrowLeft size={18} color={colors.gray[700]} />
          </button>
          <Button onClick={handleFinish}>
            Guardar y salir
          </Button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        style={{
          width: '100%',
          maxWidth: 430,
          minHeight: 'calc(100vh - 1.5rem)',
          backgroundColor: colors.white,
          border: `1px solid ${colors.gray[200]}`,
          borderRadius: 28,
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            padding: `${spacing[5]} ${spacing[4]} ${spacing[3]}`,
            backgroundColor: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(8px)',
            borderBottom: `1px solid ${colors.gray[100]}`,
          }}
        >
          <div style={{ marginBottom: spacing[3] }}>
            <h1
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: colors.gray[900],
                marginBottom: spacing[1],
              }}
            >
              Perfil profesional
            </h1>
            <p style={{ fontSize: 13, color: colors.gray[600] }}>
              Completa cada paso para mejorar tus oportunidades.
            </p>
          </div>

          <div style={{ marginBottom: spacing[3] }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing[1] }}>
              <span style={{ fontSize: 12, color: colors.gray[500], fontWeight: 600 }}>Progreso general</span>
              <span style={{ fontSize: 12, color: colors.primary[600], fontWeight: 700 }}>{totalProgress}%</span>
            </div>
            <div
              style={{
                width: '100%',
                height: 8,
                backgroundColor: colors.gray[100],
                borderRadius: 999,
                overflow: 'hidden',
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${totalProgress}%` }}
                transition={{ duration: 0.5 }}
                style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${colors.primary[500]} 0%, ${colors.secondary[500]} 100%)`,
                }}
              />
            </div>
          </div>

          <nav
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(categories.length, 1)}, minmax(0, 1fr))`,
              gap: spacing[2],
            }}
          >
            {categories.map((category) => {
              const progress = getCategoryProgress(category.category);
              const isActive = activeCategory === category.category;
              const icon = categoryIcons[category.category];

              return (
                <motion.button
                  key={category.category}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => changeCategory(category.category)}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: spacing[1],
                    padding: spacing[2],
                    borderRadius: 12,
                    border: `1px solid ${isActive ? colors.primary[300] : colors.gray[200]}`,
                    backgroundColor: isActive ? colors.primary[50] : colors.white,
                    color: isActive ? colors.primary[700] : colors.gray[600],
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 10,
                      backgroundColor: isActive ? colors.primary[500] : colors.gray[100],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isActive ? colors.white : icon.color,
                    }}
                  >
                    {icon.icon}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                    {category.label.split(' ')[0]}
                  </span>
                  {progress === 100 ? (
                    <CheckCircle size={12} color={colors.success?.[500] || '#10b981'} />
                  ) : (
                    <Circle size={10} color={colors.gray[400]} />
                  )}
                </motion.button>
              );
            })}
          </nav>
        </div>

        <motion.main
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            flex: 1,
            padding: `${spacing[4]} ${spacing[4]} ${spacing[8]}`,
            overflowY: 'auto',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
            >
              {renderCategoryContent()}
            </motion.div>
          </AnimatePresence>
        </motion.main>
        <div
          style={{
            padding: spacing[4],
            borderTop: `1px solid ${colors.gray[100]}`,
            display: 'flex',
            justifyContent: 'space-between',
            gap: spacing[2],
            backgroundColor: colors.white,
          }}
        >
          <button
            onClick={goBack}
            disabled={isFirst}
            style={{
              flex: 1,
              padding: spacing[3],
              borderRadius: 12,
              border: `1px solid ${colors.gray[300]}`,
              backgroundColor: isFirst ? colors.gray[100] : colors.white,
              color: colors.gray[700],
              cursor: isFirst ? 'not-allowed' : 'pointer',
              fontWeight: 600,
            }}
          >
            Volver
          </button>

          <button
            onClick={goNext}
            style={{
              flex: 1,
              padding: spacing[3],
              borderRadius: 12,
              border: 'none',
              background: `linear-gradient(90deg, ${colors.primary[500]} 0%, ${colors.secondary[500]} 100%)`,
              color: colors.white,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {isLast ? 'Finalizar' : 'Continuar'}
          </button>
        </div>

      </motion.div>
    </div>
  );
}
